import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { caixaService, transactionService } from '../../../shared/services';
import { IPropsBusinessPage } from '../Business';
import { dateFormat } from '../../../shared/helpers/dateFormat';
import { usePagination } from '../../../shared/hooks/usePagination';
import { SconTablePagination } from '../../../shared/components/UI';

const formatCurrency = (value: number) => `R$ ${value.toFixed(2)}`;

const predefinedCategories = [
  'Material',
  'Aluguel',
  'Luz',
  'Água',
  'Internet',
  'Marketing',
  'Salário',
  'Manutenção',
  'Outros'
];

export const BusinessTransactions: React.FC<IPropsBusinessPage> = ({ business }) => {
  const queryClient = useQueryClient();
  const [type, setType] = useState<number>(1);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmEntry, setDeleteConfirmEntry] = useState<number | null>(null);

  const { data: currentCaixa, isFetching: isFetchingCaixa } = useQuery({
    queryKey: ['currentCaixa', business.id],
    queryFn: () => caixaService.getCurrent(business.id),
  });

  const pagination = usePagination();

  const { data: pagedResult, isFetching } = useQuery({
    queryKey: ['transactions', business.id, pagination.page, pagination.rowsPerPage],
    queryFn: () => transactionService.getByBusiness(business.id, pagination.page + 1, pagination.rowsPerPage),
  });
  const entries = pagedResult?.items;
  const totalCount = pagedResult?.totalCount ?? 0;

  const { mutate: createEntry, isLoading: isCreating } = useMutation({
    mutationFn: () => transactionService.create({
      businessId: business.id,
      caixaId: currentCaixa?.id,
      type,
      amount: parseFloat(amount) || 0,
      description,
      category: category || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      setAmount('');
      setDescription('');
      setCategory('');
      setType(1);
      setModalOpen(false);
    },
  });

  const { mutate: deleteEntry, isLoading: isDeleting } = useMutation({
    mutationFn: (id: number) => transactionService.remove(id, business.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });

  const totalIncome = pagedResult?.summary?.totalIncome ?? 0;
  const totalExpense = pagedResult?.summary?.totalExpense ?? 0;

  const isLoading = isFetching || isFetchingCaixa;

  if (isLoading) {
    return <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>;
  }

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      {!currentCaixa && (
        <Alert severity="warning">
          Nenhum caixa aberto no momento. Abra um caixa para adicionar movimentações manuais.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant='caption' color='text.secondary'>Total Entradas</Typography>
              <Typography variant='h6' color='success.main'>{formatCurrency(totalIncome)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant='caption' color='text.secondary'>Total Saídas</Typography>
              <Typography variant='h6' color='error.main'>{formatCurrency(totalExpense)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box display='flex'>
        <Button
          variant='contained'
          onClick={() => setModalOpen(true)}
          disabled={!currentCaixa}
          fullWidth
        >
          Nova movimentação
        </Button>
      </Box>

      {/* Add Movement Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Adicionar Movimentação Manual</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={type}
                  label='Tipo'
                  onChange={(e) => setType(e.target.value as number)}
                >
                  <MenuItem value={0}>Entrada</MenuItem>
                  <MenuItem value={1}>Saída</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Valor (R$)'
                placeholder='0,00'
                value={Number(amount) > 0 ? Number(amount).toFixed(2) : ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  setAmount(raw ? String(parseFloat(raw) / 100) : '');
                }}
                fullWidth
                InputProps={{
                  startAdornment: <Typography variant='body2' sx={{ mr: 0.5, color: 'text.secondary' }}>R$</Typography>,
                }}
                inputProps={{
                  inputMode: 'decimal',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Descrição'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={category}
                  label='Categoria'
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <MenuItem value=''><em>Selecione</em></MenuItem>
                  {predefinedCategories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button
            variant='contained'
            onClick={() => createEntry()}
            disabled={isCreating || !amount || !description}
          >
            {isCreating ? 'Adicionando...' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmEntry !== null} onClose={() => setDeleteConfirmEntry(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir esta movimentação? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmEntry(null)}>Cancelar</Button>
          <Button
            variant='contained'
            color='error'
            onClick={() => {
              if (deleteConfirmEntry !== null) {
                deleteEntry(deleteConfirmEntry);
                setDeleteConfirmEntry(null);
              }
            }}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant='h6' mt={2}>Histórico de Movimentações Manuais</Typography>
      
      {entries && entries.length > 0 ? (
        <>
          {entries.map((entry) => (
            <Card key={entry.id}>
              <CardContent>
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Box>
                    <Chip
                      label={entry.typeName}
                      color={entry.type === 0 ? 'success' : 'error'}
                      size='small'
                      sx={{ mr: 1 }}
                    />
                    {entry.category && (
                      <Chip label={entry.category} size='small' variant='outlined' sx={{ mr: 1 }} />
                    )}
                    {entry.caixaInfo && (
                      <Chip label={entry.caixaInfo} size='small' variant='outlined' color='secondary' sx={{ mr: 1 }} />
                    )}
                    <Typography variant='body1' sx={{ mt: 1 }}>{entry.description}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {entry.createdByName?.trim() ? `${entry.createdByName} - ` : ''}{dateFormat(entry.createdAt, 'DD/MM/YYYY HH:mm')}
                    </Typography>
                  </Box>
                  <Box display='flex' alignItems='center' gap={2}>
                    <Typography
                      variant='h6'
                      color={entry.type === 0 ? 'success.main' : 'error.main'}
                    >
                      {entry.type === 0 ? '+' : '-'}{formatCurrency(entry.amount)}
                    </Typography>
                    <Tooltip title={entry.caixaIsOpen === false ? 'Caixa fechado não pode ser modificado' : 'Excluir movimentação'}>
                      <span>
                        <Button
                          size='small'
                          color='error'
                          onClick={() => setDeleteConfirmEntry(entry.id)}
                          disabled={isDeleting || entry.caixaIsOpen === false}
                        >
                          ✕
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
          <SconTablePagination
            count={totalCount}
            {...pagination.tablePaginationProps}
          />
        </>
      ) : (
        <Typography color='text.secondary' textAlign='center' py={4}>
          Nenhuma movimentação encontrada
        </Typography>
      )}
    </Box>
  );
};